import type { User } from '../../scenes/authentication/types/types.auth';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import posthog from 'posthog-js';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@shared/services/AuthService';
import { formatUTMDataForUser, getStoredUTMData } from '@shared/utils/utmTracking';

// Check if a token is expiring soon (within bufferMs milliseconds)
function isTokenExpiringSoon(token: string, bufferMs = 5 * 60 * 1000): boolean {
  try {
    const decoded = jwtDecode(token) as { exp?: number };
    if (!decoded.exp) return false;
    const expiresAt = decoded.exp * 1000;
    return Date.now() + bufferMs > expiresAt;
  } catch {
    return true; // If we can't decode, assume it's expiring
  }
}

export enum UserLoadState {
  None = 'none',
  Unauthenticated = 'unauthenticated',
  Authenticated = 'authenticated',
}

export interface UserContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  updateUser: (
    value: User | null | ((prev: User | null) => User | null),
    opts?: { skipSessionRefresh?: boolean }
  ) => Promise<void>;
  refetchUser: (override?: { email: string; idToken: string }) => Promise<void>;
  fetchUsageInfo: () => Promise<void>;
  userLoadState: UserLoadState;
  ssoLogin: () => Promise<void>;
  logout: () => void;
  isLoggedIn: boolean;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  updateUser: async () => {},
  refetchUser: async () => {},
  fetchUsageInfo: async () => {},
  userLoadState: UserLoadState.None,
  ssoLogin: async () => {},
  logout: () => {},
  isLoggedIn: false,
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, _setRawUser] = useState<User | null>(null);
  const [userLoadState, setUserLoadState] = useState(UserLoadState.None);
  const currentRequest = useRef<Promise<void> | null>(null);

  const setRawUser = (value: React.SetStateAction<User | null>) => {
    _setRawUser(value);
  };

  const isLoggedIn = !!(user && user.idToken);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSSOLoginInProgress = useRef(false);

  const updateUser = async (
    value: User | null | ((prev: User | null) => User | null),
    opts?: { skipSessionRefresh?: boolean }
  ) => {
    const updatedUser = typeof value === 'function' ? value(user) : value;

    if (!updatedUser) {
      return setRawUser(null);
    }

    try {
      if (!opts?.skipSessionRefresh && updatedUser.loginMethod !== 'sso') {
        const session = await authService.refreshSession();
        updatedUser.idToken = session.getIdToken().getJwtToken();
        updatedUser.accessToken = session.getAccessToken().getJwtToken();

        const decoded = updatedUser.idToken
          ? (jwtDecode(updatedUser.idToken) as { [key: string]: any })
          : null;
        updatedUser.isAdmin =
          decoded?.['cognito:groups']?.includes('admin') || false;
      }

      setRawUser((prev) => updatedUser || prev);
    } catch {
      setRawUser(updatedUser);
      navigate('/sign-in');
    }
  };

  const fetchUsageInfo = async (fromUser?: User) => {
    const current = fromUser || user;
    if (!current?.idToken) {
      return;
    }

    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/subscription/usage`,
        { headers: { Authorization: current.idToken } }
      );
      const usage = res.data.usage;
      const subscription = res.data.subscription;

      setRawUser((prev) => {
        const base = prev ?? current;
        if (!base) return prev;

        const plan = subscription?.props?.plan;
        const subscriptionStatus = subscription?.props?.status ?? null;

        const deepDiveLimit =
          subscription?.props?.features?.numberOfDeepDiveViews ?? null;
        const viewed = usage?.deepDivesViewedInSubPeriod ?? 0;
        const submitted = usage?.deepDivesSubmittedInSubPeriod ?? 0;
        const available = usage?.deepDivesAvailableInSubPeriod ?? null;
        const irAvailable =
          usage?.intelligenceRequestsAvailableInSubPeriod ?? 0;

        const updatedUser = {
          ...base,
          planSlug: plan,
          subscriptionStatus,
          deepDivesViewedInSubPeriod: viewed,
          planDeepDiveReviewLimit: deepDiveLimit,
          planIntelligenceRequestsLimit:
            subscription?.props?.features?.numberOfIntelligenceRequests,
          deepDivesSubmittedInSubPeriod: submitted,
          deepDivesAvailableInSubPeriod: available,
          maxConsumableDeepDivesInSubPeriod: deepDiveLimit == null
            ? null
            : deepDiveLimit + submitted,
          intelligenceRequestsAvailableInSubPeriod: irAvailable,
        };

        return updatedUser;
      });
    } catch {
      // Silently ignore usage fetch errors
    }
  };

  const refetchUser = async (override?: { email: string; idToken: string }) => {
    if (currentRequest.current) {
      return currentRequest.current;
    }
    const email = override?.email || user?.email;
    let token = override?.idToken || user?.idToken;

    if (!email || !token) {
      return;
    }

    currentRequest.current = (async () => {
      const maxRetries = 2;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          // Check if token is expiring soon and refresh proactively
          if (isTokenExpiringSoon(token)) {
            const session = await authService.refreshSession();
            token = session.getIdToken().getJwtToken();
          }

          const res = await axios.get(
            `${import.meta.env.VITE_API_BASE_URL}/user/${encodeURIComponent(email)}`,
            { headers: { Authorization: token } }
          );

          // Try to refresh the session for fresh tokens, but fall back to
          // the token we already have if the Cognito session isn't available
          // yet (e.g. right after SSO login).
          let activeIdToken = token;
          let activeAccessToken = token;
          try {
            const session = await authService.refreshSession();
            activeIdToken = session.getIdToken().getJwtToken();
            activeAccessToken = session.getAccessToken().getJwtToken();
          } catch {
            // Session refresh failed — use the token that already worked
            // for the API call above. This is common after SSO onboarding
            // when Cognito's local session may not be fully established.
          }

          const freshUser: User = {
            ...res.data,
            idToken: activeIdToken,
            accessToken: activeAccessToken,
            hasCompletedRegistration: true,
          };

          const decoded: any = freshUser.idToken
            ? jwtDecode(freshUser.idToken)
            : null;
          setRawUser({
            ...freshUser,
            isAdmin: decoded?.['cognito:groups']?.includes('admin') || false,
          });
          setUserLoadState(UserLoadState.Authenticated);

          await fetchUsageInfo(freshUser);
          Sentry.setUser({ id: freshUser.id, email: freshUser.email });
          posthog.identify(String(freshUser.id), { email: freshUser.email });

          // Success - exit the retry loop
          return;
        } catch (err: any) {
          const status = err?.response?.status;
          const isAuthError = status === 401 || status === 403;

          // If auth error and we have retries left, try refreshing token
          if (isAuthError && attempt < maxRetries - 1) {
            try {
              const session = await authService.refreshSession();
              token = session.getIdToken().getJwtToken();
              continue; // Retry with fresh token
            } catch {
              // Fall through to logout
            }
          }

          // Final attempt failed or non-retryable error - logout
          setRawUser(null);
          authService.logout();
          setUserLoadState(UserLoadState.Unauthenticated);

          Sentry.captureException(err, {
            tags: {
              section: 'user_data_fetch',
              email,
              attempt: attempt + 1,
              status: status || 'unknown',
            },
          });

          throw new Error('Failed to load user data');
        }
      }
    })();

    try {
      return await currentRequest.current;
    } finally {
      currentRequest.current = null;
    }
  };

  const ssoLogin = async () => {
    try {
      isSSOLoginInProgress.current = true;

      // Track SSO login attempt start
      Sentry.addBreadcrumb({
        message: 'SSO login attempt started',
        category: 'auth',
        level: 'info',
      });

      const code =
        sessionStorage.getItem('ssoCode') || searchParams.get('code') || '';
      if (code) {
        sessionStorage.setItem('ssoCode', code);
        searchParams.delete('code');
        setSearchParams(searchParams);
      }

      // Determine provider from URL or session storage
      const currentUrl = window.location.href;
      let provider = 'linkedin'; // default
      if (
        currentUrl.includes('Google') ||
        sessionStorage.getItem('oauthProvider') === 'Google'
      ) {
        provider = 'google';
      }

      Sentry.setTag('sso_provider', provider);
      Sentry.addBreadcrumb({
        message: `SSO login with ${provider}`,
        category: 'auth',
        level: 'info',
        data: { provider, hasCode: !!code },
      });

      let url = `${import.meta.env.VITE_API_BASE_URL}/auth/sso/${provider}?code=${code}`;

      // Always send the redirect URI so the API can use it in the Cognito token exchange
      url += `&cognitoRedirectUri=${encodeURIComponent(import.meta.env.VITE_COGNITO_REDIRECT_URI)}`;

      // Add UTM data to SSO request (includes referrerCode if present)
      const storedUTMData = getStoredUTMData();
      const utmPayload = formatUTMDataForUser(storedUTMData);

      if (utmPayload.referer) {
        url += `&refererCode=${encodeURIComponent(utmPayload.referer)}`;
      }

      if (utmPayload.source) {
        url += `&source=${encodeURIComponent(utmPayload.source)}`;
      }

      if (utmPayload.campaign) {
        url += `&campaign=${encodeURIComponent(utmPayload.campaign)}`;
      }

      if (utmPayload.landingPage) {
        url += `&landingPage=${encodeURIComponent(utmPayload.landingPage)}`;
      }

      Sentry.addBreadcrumb({
        message: 'Making SSO authentication request to API',
        category: 'auth',
        level: 'info',
      });

      const res = await axios.get(url);
      const { user, accessToken, idToken, refreshToken } = res.data;

      Sentry.addBreadcrumb({
        message: 'SSO authentication API request successful',
        category: 'auth',
        level: 'info',
        data: { hasUser: !!user, userEmail: user?.email },
      });

      Sentry.addBreadcrumb({
        message: 'Setting Cognito session from SSO',
        category: 'auth',
        level: 'info',
      });

      const sessionUser = await authService.setCognitoSessionFromSSO({
        email: user.email,
        accessToken,
        idToken,
        refreshToken,
      });

      Sentry.addBreadcrumb({
        message: 'Cognito session set successfully, updating user context',
        category: 'auth',
        level: 'info',
      });

      await updateUser(
        {
          ...sessionUser,
          loginMethod: 'sso',
          hasCompletedRegistration: !!user.email,
        },
        { skipSessionRefresh: true }
      );

      // Refetch user data to ensure complete profile
      try {
        await refetchUser({
          email: sessionUser.email,
          idToken: sessionUser.idToken || '',
        });

        sessionStorage.removeItem('ssoCode');
        sessionStorage.removeItem('oauthProvider');
        setUserLoadState(UserLoadState.Authenticated);

        Sentry.addBreadcrumb({
          message: 'SSO login completed successfully',
          category: 'auth',
          level: 'info',
        });
      } catch (fetchError) {
        sessionStorage.removeItem('ssoCode');
        sessionStorage.removeItem('oauthProvider');
        navigate('/sign-in');
        throw fetchError;
      }
    } catch (err) {
      // Capture the SSO error in Sentry with context
      const errorUrl = window.location.href;
      const errorProvider = errorUrl.includes('Google') ? 'google' : 'linkedin';
      Sentry.captureException(err, {
        tags: {
          section: 'sso_login',
          sso_provider: errorProvider,
        },
        contexts: {
          sso: {
            hasCode: !!searchParams.get('code'),
            currentUrl: errorUrl,
            provider: errorProvider,
          },
        },
      });

      sessionStorage.removeItem('ssoCode');
      sessionStorage.removeItem('oauthProvider');
      setUserLoadState(UserLoadState.Unauthenticated);
    } finally {
      isSSOLoginInProgress.current = false;
    }
  };

  const logout = () => {
    setRawUser(null);
    authService.logout();
    setUserLoadState(UserLoadState.Unauthenticated);
    navigate('/sign-in', { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        const current = await authService.getCurrentUser();

        if (current) {
          await updateUser(current);
          await fetchUsageInfo(current);
          setUserLoadState(UserLoadState.Authenticated);
        } else {
          setUserLoadState(UserLoadState.Unauthenticated);
        }
      } catch (err) {
        setUserLoadState(UserLoadState.Unauthenticated);
      }
    })();
  }, []);

  // Refresh tokens every 45 minutes (Cognito tokens expire after 60 minutes)
  // This gives a 15-minute buffer to prevent token expiration issues
  useEffect(() => {
    const interval = setInterval(
      async () => {
        try {
          const session = await authService.refreshSession();
          const idToken = session.getIdToken().getJwtToken();
          const accessToken = session.getAccessToken().getJwtToken();
          setRawUser((prev) => prev ? { ...prev, idToken, accessToken } : prev);
        }
        catch {}
      },
      45 * 60 * 1000 // 45 minutes
    );
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: setRawUser,
        updateUser,
        refetchUser,
        fetchUsageInfo: () => fetchUsageInfo(),
        userLoadState,
        ssoLogin,
        logout,
        isLoggedIn,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
